import {
  FormAttribute,
  FORM_ATTR_RELATION_PARENT_ID,
  getFilterByParentRule
} from '@eview/core/domain/form/form-attribute';
import { Tag, Tags } from './tag';

export interface TagWithChildren extends Tag {
  children: TagWithChildren[];
}

/** O(n) index over a tag catalog for O(1) parent/child lookups. */
export interface TagIndex {
  byId: Map<number, Tag>;
  byTaxonomy: Map<string, Tag[]>;
  /** Key: `${parentId}\0${taxonomy}` → child tags of that taxonomy under parent. */
  childrenByParentAndTaxonomy: Map<string, Tag[]>;
  /** Child tag id → parent tag id (null if root). */
  parentById: Map<number, number | null>;
}

export interface TagParentSelectOption {
  value: number;
  label: string;
}

const parentTaxonomyKey = (parentId: number, taxonomy: string): string =>
  `${parentId}\0${taxonomy}`;

const getTagParentId = (tag?: Tag | null): number | null => {
  if (!tag) {
    return null;
  }
  if (tag.parent_id != null && Number(tag.parent_id) > 0) {
    return Number(tag.parent_id);
  }
  if (tag.parent?.id != null && Number(tag.parent.id) > 0) {
    return Number(tag.parent.id);
  }
  return null;
};

/** Build maps in a single pass over tags.results. */
const buildTagIndex = (tags: Tags | null | undefined): TagIndex => {
  const byId = new Map<number, Tag>();
  const byTaxonomy = new Map<string, Tag[]>();
  const childrenByParentAndTaxonomy = new Map<string, Tag[]>();
  const parentById = new Map<number, number | null>();

  const results = tags?.results;
  if (!results?.length) {
    return { byId, byTaxonomy, childrenByParentAndTaxonomy, parentById };
  }

  for (let i = 0; i < results.length; i++) {
    const tag = results[i];
    if (!tag || tag.id == null) {
      continue;
    }
    const id = Number(tag.id);
    byId.set(id, tag);

    const taxonomy = String(tag.type || '');
    let taxList = byTaxonomy.get(taxonomy);
    if (!taxList) {
      taxList = [];
      byTaxonomy.set(taxonomy, taxList);
    }
    taxList.push(tag);

    const parentId = getTagParentId(tag);
    parentById.set(id, parentId);
    if (parentId != null) {
      const bucketKey = parentTaxonomyKey(parentId, taxonomy);
      let kids = childrenByParentAndTaxonomy.get(bucketKey);
      if (!kids) {
        kids = [];
        childrenByParentAndTaxonomy.set(bucketKey, kids);
      }
      kids.push(tag);
    }
  }

  return { byId, byTaxonomy, childrenByParentAndTaxonomy, parentById };
};

const childrenForParent = (
  index: TagIndex,
  parentId: number,
  taxonomy: string
): Tag[] =>
  index.childrenByParentAndTaxonomy.get(parentTaxonomyKey(parentId, taxonomy)) ||
  [];

/**
 * All tags of a taxonomy (including those with a cross-taxonomy parent).
 * O(1) map lookup — does not hide assigned children the way ProcessForRendering roots do.
 */
const listTaxonomyInventory = (index: TagIndex, taxonomy: string): Tag[] =>
  index.byTaxonomy.get(taxonomy) || [];

/**
 * Derive childTaxonomy → Set(parentTaxonomy) from filter_by_parent rules.
 * Two linear passes + Map lookups (no nested scans). Ambiguous duplicate keys are skipped.
 */
const deriveAllowedParentTaxonomies = (
  attributes: FormAttribute[] | null | undefined
): Map<string, Set<string>> => {
  const result = new Map<string, Set<string>>();
  if (!attributes?.length) {
    return result;
  }

  const byKey = new Map<string, FormAttribute | null>();
  for (let i = 0; i < attributes.length; i++) {
    const attr = attributes[i];
    if (!attr?.key) {
      continue;
    }
    if (byKey.has(attr.key)) {
      byKey.set(attr.key, null);
    } else {
      byKey.set(attr.key, attr);
    }
  }

  for (let i = 0; i < attributes.length; i++) {
    const child = attributes[i];
    if (!child || child.type !== 'tags') {
      continue;
    }
    const rule = getFilterByParentRule(child.config);
    const parentKey = rule?.target?.key;
    if (!parentKey) {
      continue;
    }
    const relation =
      rule.relation ?? rule.target?.value ?? FORM_ATTR_RELATION_PARENT_ID;
    if (relation !== FORM_ATTR_RELATION_PARENT_ID) {
      continue;
    }
    const parent = byKey.get(parentKey);
    if (!parent || parent.type !== 'tags') {
      continue;
    }
    const childTax = tagTaxonomyKeyFromFormAttribute(child);
    const parentTax = tagTaxonomyKeyFromFormAttribute(parent);
    if (!childTax || !parentTax) {
      continue;
    }
    let set = result.get(childTax);
    if (!set) {
      set = new Set<string>();
      result.set(childTax, set);
    }
    set.add(parentTax);
  }

  return result;
};

/** Parent candidates from allowed taxonomies only; sorted once. O(k log k). */
const buildParentSelectOptions = (
  index: TagIndex,
  parentTaxonomies: Iterable<string> | null | undefined
): TagParentSelectOption[] => {
  const options: TagParentSelectOption[] = [];
  if (!parentTaxonomies) {
    return options;
  }
  const seen = new Set<number>();
  for (const taxonomy of parentTaxonomies) {
    const list = index.byTaxonomy.get(taxonomy);
    if (!list?.length) {
      continue;
    }
    for (let i = 0; i < list.length; i++) {
      const tag = list[i];
      const id = Number(tag.id);
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      options.push({ value: id, label: String(tag.tag ?? id) });
    }
  }
  options.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
  return options;
};

const getParentDisplayName = (
  index: TagIndex,
  child: Tag | null | undefined
): string | null => {
  const parentId = getTagParentId(child);
  if (parentId == null) {
    return null;
  }
  const parent = index.byId.get(parentId);
  return parent?.tag ? String(parent.tag) : `#${parentId}`;
};

const SortListAlphabetical = (list: any) => {
  return list;
};

const CategoriesRandomColor = () => {
  var color = Math.floor(0x1000000 * Math.random()).toString(16);
  return '#' + ('000000' + color).slice(-6);
};

const FormattedTagName = (name: any) => {
  return String(name ?? '')
    .toLowerCase()
    .replaceAll(' ', '-');
};

/** Same taxonomy key used when filtering tags for a field (see form-attribute-base nestedTags$). */
export type TagTaxonomyFormField = {
  input: string;
  label?: string;
  type: string;
  key?: string;
  config?: FormAttribute['config'];
};

const tagTaxonomyKeyFromFormAttribute = (c: TagTaxonomyFormField): string => {
  if (!c) return '';
  let input = c.input;
  if (input === 'tags') {
    input = FormattedTagName(c.label ?? '');
  }
  return input;
};

/** One entry per taxonomy: multiple forms may reuse the same label/input (e.g. "Institución"). */
const dedupeFormAttributesByTagTaxonomy = (
  attrs: TagTaxonomyFormField[]
): TagTaxonomyFormField[] => {
  const byKey = new Map<string, TagTaxonomyFormField>();
  for (const a of attrs) {
    if (a.type !== 'tags') continue;
    const key = tagTaxonomyKeyFromFormAttribute(a);
    if (!byKey.has(key)) {
      byKey.set(key, a);
    }
  }
  return Array.from(byKey.values()).sort((a, b) =>
    String(a.label ?? '').localeCompare(String(b.label ?? ''), undefined, {
      sensitivity: 'base'
    })
  );
};

/**
 * Tree for a taxonomy using adjacency from TagIndex (O(n) build of index + O(k) walk).
 * When `flatRoots` is provided, those tags are rendered as roots (parent stripped) —
 * used for cross-taxonomy parent filters (e.g. ODPE → Local).
 *
 * For admin inventory that must include cross-taxonomy children, use listTaxonomyInventory.
 */
const ProcessForRendering = (
  tags: Tags,
  formAttr,
  index?: TagIndex,
  flatRoots?: Tag[]
): TagWithChildren[] => {
  let inputType =
    formAttr && formAttr.input === 'tags' ? 'tags' : formAttr.input;
  if (!tags) return null;

  const tagIndex = index || buildTagIndex(tags);
  const selectedVal =
    formAttr.value && formAttr.value[0] !== undefined ? formAttr.value[0] : 0;

  const childrenOf = (parentId: number): TagWithChildren[] => {
    const kids = childrenForParent(tagIndex, parentId, inputType);
    const sorted = [...kids].sort((t0, t1) =>
      t0.priority === t1.priority ? 0 : t1.priority < t0.priority ? 1 : -1
    );
    return sorted.map(t => ({
      ...t,
      val: selectedVal,
      children: childrenOf(Number(t.id))
    }));
  };

  let roots: Tag[];
  if (flatRoots) {
    roots = flatRoots;
  } else {
    const inTax = tagIndex.byTaxonomy.get(inputType) || [];
    // Include same-taxonomy roots AND tags whose parent is outside this taxonomy
    // so cross-taxonomy children remain visible in management UIs.
    roots = inTax.filter(t => {
      const pid = getTagParentId(t);
      if (pid == null) {
        return true;
      }
      const parent = tagIndex.byId.get(pid);
      return !parent || String(parent.type || '') !== inputType;
    });
  }

  const topParents = roots.map(t => ({
    ...t,
    val: selectedVal,
    children: flatRoots ? ([] as TagWithChildren[]) : childrenOf(Number(t.id))
  }));

  return SortListAlphabetical(topParents);
};

/** Opciones para reglas condicionales / selects según taxonomía del atributo. */
const getTagOptionsForFormAttribute = (
  tags: Tags | null | undefined,
  formAttribute: TagTaxonomyFormField
): { value: string; text: string }[] => {
  if (!tags?.results?.length || !formAttribute) {
    return [];
  }
  const taxonomyKey = tagTaxonomyKeyFromFormAttribute(formAttribute);
  const index = buildTagIndex(tags);
  const list = index.byTaxonomy.get(taxonomyKey) || [];
  return list.map(t => ({ value: t.id.toString(), text: t.tag }));
};

const FlatifyChildren = (
  tags: TagWithChildren[]
): { id: number; children: number[] }[] => {
  const flatify = (tag: TagWithChildren): number[] => {
    return tag.children
      .map(t => {
        return [t.id, ...flatify(t)];
      })
      .reduce((flt, t) => (flt = [...flt, ...t]), []);
  };
  return tags.map(t => ({
    id: t.id,
    children: flatify(t)
  }));
};

/** Escape a CSV field (RFC-style quotes). */
const escapeCsvField = (value: string | number | null | undefined): string => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

/**
 * Build a browser-side CSV template.
 * - child mode: parent_id,parent_name,tag (one blank row per allowed parent)
 * - root mode: tag (header + a few empty sample rows)
 */
const buildTagImportTemplateCsv = (
  parents: Array<{ id: number; label: string }> | null | undefined,
  mode: 'root' | 'child' = 'child'
): string => {
  if (mode === 'root') {
    return ['tag', '', '', ''].join('\r\n') + '\r\n';
  }
  const lines = ['parent_id,parent_name,tag'];
  if (!parents?.length) {
    return lines.join('\r\n') + '\r\n';
  }
  for (let i = 0; i < parents.length; i++) {
    const parent = parents[i];
    if (!parent || !(parent.id > 0)) {
      continue;
    }
    lines.push(
      [
        escapeCsvField(parent.id),
        escapeCsvField(parent.label),
        ''
      ].join(',')
    );
  }
  return lines.join('\r\n') + '\r\n';
};

const TAG_IMPORT_MAX_BYTES = 20 * 1024 * 1024;

export type TagImportUiPhase =
  | 'idle'
  | 'uploading'
  | 'previewing'
  | 'ready'
  | 'committing'
  | 'done'
  | 'failed';

/** Derive modal phase from export_job status for preview/commit polling. */
const resolveTagImportPhase = (
  mode: 'preview' | 'commit',
  status: string | null | undefined
): TagImportUiPhase => {
  const st = String(status || '').toUpperCase();
  if (mode === 'preview') {
    if (st === 'PREVIEW_READY') {
      return 'ready';
    }
    if (st === 'FAILED') {
      return 'failed';
    }
    return 'previewing';
  }
  if (st === 'SUCCESS') {
    return 'done';
  }
  if (st === 'FAILED') {
    return 'failed';
  }
  return 'committing';
};

const canCommitTagImport = (input: {
  status?: string | null;
  can_commit?: boolean;
  invalid?: number;
  create?: number;
}): boolean => {
  if (!input) {
    return false;
  }
  if (input.can_commit === true) {
    return true;
  }
  return (
    String(input.status || '').toUpperCase() === 'PREVIEW_READY' &&
    (input.invalid || 0) === 0 &&
    (input.create || 0) > 0
  );
};

const importRowsTotalPages = (total: number, pageSize: number): number => {
  const size = pageSize > 0 ? pageSize : 1;
  return Math.max(1, Math.ceil(Math.max(0, total) / size));
};

/** Client-side file gate; backend remains authoritative. */
const validateTagImportFile = (
  file: File | null | undefined
): string | null => {
  if (!file) {
    return 'TAGS_IMPORT_ERR_NO_FILE';
  }
  const name = (file.name || '').toLowerCase();
  if (!name.endsWith('.csv')) {
    return 'TAGS_IMPORT_ERR_EXTENSION';
  }
  const mime = (file.type || '').toLowerCase();
  if (
    mime &&
    mime !== 'text/csv' &&
    mime !== 'text/plain' &&
    mime !== 'application/csv' &&
    mime !== 'application/vnd.ms-excel' &&
    mime !== 'application/octet-stream'
  ) {
    return 'TAGS_IMPORT_ERR_MIME';
  }
  if (file.size <= 0) {
    return 'TAGS_IMPORT_ERR_EMPTY';
  }
  if (file.size > TAG_IMPORT_MAX_BYTES) {
    return 'TAGS_IMPORT_ERR_SIZE';
  }
  return null;
};

export const TagHelpers = {
  ProcessForRendering,
  FlatifyChildren,
  SortListAlphabetical,
  FormattedTagName,
  CategoriesRandomColor,
  tagTaxonomyKeyFromFormAttribute,
  dedupeFormAttributesByTagTaxonomy,
  getTagOptionsForFormAttribute,
  buildTagIndex,
  childrenForParent,
  getTagParentId,
  listTaxonomyInventory,
  deriveAllowedParentTaxonomies,
  buildParentSelectOptions,
  getParentDisplayName,
  escapeCsvField,
  buildTagImportTemplateCsv,
  validateTagImportFile,
  TAG_IMPORT_MAX_BYTES,
  resolveTagImportPhase,
  canCommitTagImport,
  importRowsTotalPages
};
